import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

export interface DashboardState {
  activeTab: string;
  crossTabData: Record<string, any>;
  sharedFilters: SharedFilters;
  globalNotifications: DashboardNotification[];
  userPreferences: UserDashboardPreferences;
  sessionContext: SessionContext;
  learningMomentum: LearningMomentum;
  quickActions: QuickAction[];
  recentActivity: RecentActivity[];
}

export interface SharedFilters {
  difficultyRange: [number, number];
  contentTypes: string[];
  timeRange: 'today' | 'week' | 'month' | 'all';
  disciplines: string[];
  studyTimeAvailable: number; // minutes
  learningGoals: string[];
}

export interface DashboardNotification {
  id: string;
  type: 'achievement' | 'recommendation' | 'reminder' | 'milestone' | 'social';
  title: string;
  message: string;
  timestamp: Date;
  priority: 'high' | 'medium' | 'low';
  actionable: boolean;
  action?: NotificationAction;
  metadata?: Record<string, any>;
}

export interface NotificationAction {
  label: string;
  callback: () => void;
  type: 'primary' | 'secondary' | 'destructive';
}

export interface UserDashboardPreferences {
  defaultTab: string;
  compactMode: boolean;
  showRecommendations: boolean;
  autoStartSessions: boolean;
  notificationSettings: {
    achievements: boolean;
    recommendations: boolean;
    reminders: boolean;
    social: boolean;
  };
  quickActionPreferences: string[];
  dashboardLayout: 'standard' | 'compact' | 'detailed';
  theme: string;
  notifications: boolean;
  showAdvancedFeatures: boolean;
}

export interface SessionContext {
  hasActiveSession: boolean;
  sessionId: string | null;
  sessionType: 'study' | 'review' | 'practice' | 'assessment' | null;
  elapsedTime: number;
  plannedDuration: number;
  currentActivity: string | null;
  performanceScore: number;
  focusLevel: number;
  nextBreakIn: number;
  startTime: Date;
  currentFocus: string;
  timeSpent: number;
  interactions: number;
}

export interface LearningMomentum {
  currentStreak: number;
  streakType: 'daily' | 'weekly' | 'session';
  momentum: number; // 0-100
  velocity: number; // learning rate
  trend: 'increasing' | 'stable' | 'decreasing';
  predictions: {
    nextMilestone: string;
    estimatedTime: number;
    confidence: number;
  };
  motivationalInsights: string[];
  score: number;
  lastUpdated: Date;
}

export interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: string;
  category: 'session' | 'content' | 'progress' | 'social' | 'admin';
  priority: number;
  enabled: boolean;
  estimatedTime?: number;
  prerequisites?: string[];
  callback: () => void;
  analytics: {
    usage: number;
    effectiveness: number;
    lastUsed?: Date;
  };
}

export interface RecentActivity {
  id: string;
  type: 'study_session' | 'content_completion' | 'achievement' | 'social_interaction';
  title: string;
  description: string;
  timestamp: Date;
  metadata: {
    duration?: number;
    score?: number;
    contentId?: string;
    achievementId?: string;
  };
  relatedActions: QuickAction[];
}

export interface EnhancedDashboardData {
  userProgress: any;
  recommendations: any[];
  achievements: any[];
  learningPaths: any[];
  sessionOptimization: any;
  socialData: any;
  contentDiscovery: any[];
  analytics: any;
  learningMomentum: LearningMomentum;
  nextBestAction: QuickAction | null;
  timeBasedSuggestions: QuickAction[];
  contextualActions: QuickAction[];
  achievementProgress: any[];
  personalizedInsights: string[];
  trendingContent: any[];
  personalizedSuggestions: any[];
}

interface DashboardContextType {
  dashboardState: DashboardState;
  enhancedData: EnhancedDashboardData | null;
  isLoading: boolean;
  error: Error | null;
  updateCrossTabData: (key: string, data: any) => void;
  updateSharedFilters: (filters: Partial<SharedFilters>) => void;
  updateUserPreferences: (preferences: Partial<UserDashboardPreferences>) => void;
  addNotification: (notification: Omit<DashboardNotification, 'id' | 'timestamp'>) => void;
  dismissNotification: (notificationId: string) => void;
  updateSessionContext: (context: Partial<SessionContext>) => void;
  executeQuickAction: (actionId: string) => void;
  refreshDashboardData: () => void;
  getActiveTabData: () => any;
  getRecommendationsForTab: (tabName: string) => any[];
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [dashboardState, setDashboardState] = useState<DashboardState>({
    activeTab: 'overview',
    crossTabData: {},
    userPreferences: {
      theme: 'light',
      notifications: true,
      compactMode: false,
      showAdvancedFeatures: false
    },
    learningMomentum: {
      score: 0,
      trend: 'stable',
      velocity: 0,
      lastUpdated: new Date()
    },
    sessionContext: {
      startTime: new Date(),
      currentFocus: 'general',
      timeSpent: 0,
      interactions: 0
    },
    quickActions: []
  });

  // OPTIMIZED: Enhanced dashboard data with better caching
  const { data: enhancedData, isLoading, error, refetch } = useQuery({
    queryKey: ['enhanced-dashboard', user?.id],
    queryFn: async (): Promise<EnhancedDashboardData> => {
      if (!user?.id) throw new Error('User not authenticated');

      // PERFORMANCE: Simplified parallel fetching
      try {
        const [userProgressRaw, recommendationsRaw, achievementsRaw] = await Promise.allSettled([
          // Basic user progress
          supabase
            .from('user_progress')
            .select(`
              node_id,
              progress_percentage,
              completion_status,
              time_spent,
              nodes!inner(
                id,
                title,
                discipline_id,
                disciplines(name, slug)
              )
            `)
            .eq('user_id', user.id)
            .order('last_accessed', { ascending: false })
            .limit(20), // Reduced limit

          // Basic recommendations
          supabase
            .from('nodes')
            .select(`
              id,
              title,
              content_type,
              difficulty_level,
              estimated_study_time,
              disciplines(name, slug)
            `)
            .eq('status', 'published')
            .order('created_at', { ascending: false })
            .limit(5), // Reduced limit

          // Basic achievements
          supabase
            .from('user_achievements')
            .select('*')
            .eq('user_id', user.id)
            .limit(10)
        ]);

        // Handle results safely
        const userProgress = userProgressRaw.status === 'fulfilled' ? userProgressRaw.value.data || [] : [];
        const recommendations = recommendationsRaw.status === 'fulfilled' ? recommendationsRaw.value.data || [] : [];
        const achievements = achievementsRaw.status === 'fulfilled' ? achievementsRaw.value.data || [] : [];

        return {
          userProgress,
          recommendations,
          achievements,
          learningPaths: [], // Simplified
          sessionOptimization: null,
          socialData: null,
          contentDiscovery: [],
          analytics: { totalStudyTime: 0, sessionsCompleted: 0 },
          learningMomentum: { score: 0, trend: 'stable', velocity: 0 },
          nextBestAction: null,
          timeBasedSuggestions: [],
          contextualActions: [],
          achievementProgress: [],
          personalizedInsights: [],
          trendingContent: [],
          personalizedSuggestions: recommendations.slice(0, 3)
        };
      } catch (error) {
        console.error('Dashboard data fetch error:', error);
        // Return minimal fallback
        return {
          userProgress: [],
          recommendations: [],
          achievements: [],
          learningPaths: [],
          sessionOptimization: null,
          socialData: null,
          contentDiscovery: [],
          analytics: { totalStudyTime: 0, sessionsCompleted: 0 },
          learningMomentum: { score: 0, trend: 'stable', velocity: 0 },
          nextBestAction: null,
          timeBasedSuggestions: [],
          contextualActions: [],
          achievementProgress: [],
          personalizedInsights: [],
          trendingContent: [],
          personalizedSuggestions: []
        };
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - very aggressive caching
    gcTime: 15 * 60 * 1000, // 15 minutes cache retention
    refetchInterval: false, // Disable automatic refetching
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!user?.id,
    retry: 1,
    throwOnError: false
  });

  // SIMPLIFIED quick actions
  const quickActions = useMemo(() => [
    {
      id: 'continue-learning',
      label: 'Continue Learning',
      icon: 'play',
      description: 'Resume your current learning path',
      callback: () => console.log('Continue learning'),
      enabled: true,
      analytics: { usage: 0, lastUsed: new Date() }
    },
    {
      id: 'explore-content',
      label: 'Explore Content',
      icon: 'search',
      description: 'Discover new content',
      callback: () => console.log('Explore content'),
      enabled: true,
      analytics: { usage: 0, lastUsed: new Date() }
    }
  ], []);

  // Add notification function
  const addNotification = useCallback((notification: any) => {
    console.log('Notification:', notification);
  }, []);

  // Execute quick action
  const executeQuickAction = useCallback((actionId: string) => {
    const action = quickActions.find(a => a.id === actionId);
    if (action) {
      action.callback();
      action.analytics.usage += 1;
      action.analytics.lastUsed = new Date();
    }
  }, [quickActions]);

  // Get active tab data
  const getActiveTabData = useCallback(() => {
    return dashboardState.crossTabData[dashboardState.activeTab] || null;
  }, [dashboardState.crossTabData, dashboardState.activeTab]);

  // Refresh dashboard data
  const refreshDashboardData = useCallback(() => {
    refetch();
  }, [refetch]);

  const contextValue = useMemo(() => ({
    dashboardState,
    setDashboardState,
    enhancedData,
    isLoading,
    error,
    quickActions,
    addNotification,
    executeQuickAction,
    getActiveTabData,
    refreshDashboardData
  }), [
    dashboardState,
    enhancedData,
    isLoading,
    error,
    quickActions,
    addNotification,
    executeQuickAction,
    getActiveTabData,
    refreshDashboardData
  ]);

  return (
    <DashboardContext.Provider value={contextValue}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = (): DashboardContextType => {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

// Helper functions for data processing

async function getUserProgressWithAnalytics(userId: string) {
  const { data, error } = await supabase
    .from('user_progress')
    .select(`
      *,
      nodes!inner(
        id,
        title,
        node_type,
        difficulty_level,
        estimated_study_time,
        disciplines!inner(name)
      )
    `)
    .eq('user_id', userId)
    .order('last_accessed_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

async function getAdvancedRecommendations(userId: string) {
  // This would call the advanced recommendation function
  const { data, error } = await supabase.rpc('get_advanced_recommendations', {
    user_id_param: userId,
    limit_param: 10
  });

  if (error) throw error;
  return data || [];
}

async function getEnhancedAchievements(userId: string) {
  const { data, error } = await supabase
    .from('user_achievements')
    .select(`
      *,
      achievements!inner(*)
    `)
    .eq('user_id', userId)
    .order('unlocked_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

async function getLearningPathProgress(userId: string) {
  const { data, error } = await supabase
    .from('user_learning_path_progress')
    .select(`
      *,
      learning_paths!inner(*)
    `)
    .eq('user_id', userId)
    .order('last_accessed_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

async function getSessionOptimizations(userId: string) {
  const { data, error } = await supabase
    .from('user_sessions')
    .select('*')
    .eq('user_id', userId)
    .order('start_time', { ascending: false })
    .limit(10);

  if (error) throw error;
  return data || [];
}

async function getSocialLearningData(userId: string) {
  // Mock implementation - would integrate with social features
  return {
    peers: [],
    collaborations: [],
    discussions: []
  };
}

async function getContentDiscoveryData(userId: string) {
  const { data, error } = await supabase
    .from('nodes')
    .select(`
      *,
      disciplines!inner(name)
    `)
    .eq('status', 'published')
    .order('created_at', { ascending: false })
    .limit(20);

  if (error) throw error;
  return data || [];
}

async function getDashboardAnalytics(userId: string) {
  // This would aggregate various analytics
  return {
    totalStudyTime: 0,
    sessionsCompleted: 0,
    averageScore: 0,
    streak: 0,
    trends: {}
  };
}

function calculateLearningMomentum(userProgress: any[], analytics: any): LearningMomentum {
  const recentProgress = userProgress.slice(0, 7); // Last 7 items
  const streak = calculateStreak(recentProgress);
  
  return {
    currentStreak: streak,
    streakType: 'daily',
    momentum: Math.min(100, streak * 10 + (recentProgress.length * 5)),
    velocity: calculateVelocity(recentProgress),
    trend: recentProgress.length > 3 ? 'increasing' : 'stable',
    predictions: {
      nextMilestone: 'Complete 5 more topics to unlock Advanced Learner',
      estimatedTime: 120, // minutes
      confidence: 85
    },
    motivationalInsights: generateMotivationalInsights(streak, recentProgress.length),
    score: 0,
    lastUpdated: new Date()
  };
}

function calculateStreak(progress: any[]): number {
  // Simple streak calculation based on consecutive days
  let streak = 0;
  const now = new Date();
  
  for (let i = 0; i < progress.length; i++) {
    const progressDate = new Date(progress[i].last_accessed_at);
    const daysDiff = Math.floor((now.getTime() - progressDate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysDiff === i) {
      streak++;
    } else {
      break;
    }
  }
  
  return streak;
}

function calculateVelocity(progress: any[]): number {
  if (progress.length < 2) return 0;
  
  const completedItems = progress.filter(p => p.completion_percentage >= 90);
  const timeSpan = 7; // days
  
  return Math.round((completedItems.length / timeSpan) * 100);
}

function generateMotivationalInsights(streak: number, recentActivity: number): string[] {
  const insights = [];
  
  if (streak >= 3) {
    insights.push(`You're on fire! ${streak} days in a row of learning.`);
  }
  
  if (recentActivity >= 5) {
    insights.push('Your learning velocity is impressive this week!');
  }
  
  insights.push('Consistency is key to mastering new concepts.');
  
  return insights;
}

function determineNextBestAction(userProgress: any[], recommendations: any[], sessionData: any): QuickAction | null {
  // Determine the best next action based on user state
  if (recommendations.length > 0) {
    const topRecommendation = recommendations[0];
    return {
      id: 'recommended-content',
      title: `Continue: ${topRecommendation.title}`,
      description: topRecommendation.description,
      icon: 'BookOpen',
      category: 'content',
      priority: 1,
      enabled: true,
      estimatedTime: topRecommendation.estimated_study_time,
      callback: () => {
        // Navigate to content
      },
      analytics: { usage: 0, effectiveness: 90 }
    };
  }
  
  return null;
}

function generateTimeBasedSuggestions(analytics: any, momentum: LearningMomentum): QuickAction[] {
  const currentHour = new Date().getHours();
  const suggestions: QuickAction[] = [];
  
  if (currentHour >= 9 && currentHour <= 11) {
    suggestions.push({
      id: 'morning-focus-session',
      title: 'Morning Focus Session',
      description: 'Perfect time for deep learning',
      icon: 'Sun',
      category: 'session',
      priority: 2,
      enabled: true,
      estimatedTime: 45,
      callback: () => {},
      analytics: { usage: 0, effectiveness: 85 }
    });
  }
  
  return suggestions;
}

function generateContextualActions(userProgress: any[], achievements: any[]): QuickAction[] {
  const actions: QuickAction[] = [];
  
  // Add actions based on progress and achievements
  if (userProgress.some(p => p.completion_percentage < 50)) {
    actions.push({
      id: 'resume-incomplete',
      title: 'Resume Learning',
      description: 'Continue where you left off',
      icon: 'Play',
      category: 'content',
      priority: 3,
      enabled: true,
      callback: () => {},
      analytics: { usage: 0, effectiveness: 75 }
    });
  }
  
  return actions;
}

function calculateAchievementProgress(achievements: any[]): any[] {
  // Calculate progress toward next achievements
  return achievements.filter(a => !a.unlocked_at).map(achievement => ({
    ...achievement,
    progress: calculateProgressToAchievement(achievement)
  }));
}

function calculateProgressToAchievement(achievement: any): number {
  // Mock calculation - would be based on achievement criteria
  return Math.floor(Math.random() * 80) + 10;
}

function generatePersonalizedInsights(userProgress: any[], recommendations: any[], analytics: any): string[] {
  const insights = [];
  
  if (userProgress.length > 0) {
    const avgCompletion = userProgress.reduce((sum, p) => sum + p.completion_percentage, 0) / userProgress.length;
    if (avgCompletion > 75) {
      insights.push('You have strong completion rates - excellent focus!');
    }
  }
  
  if (recommendations.length > 3) {
    insights.push('Lots of great content waiting for you to explore.');
  }
  
  return insights;
}

function getTrendingContent(contentDiscovery: any[], socialData: any): any[] {
  // Mock trending content based on engagement
  return contentDiscovery.slice(0, 5).map(content => ({
    ...content,
    trending: true,
    engagementScore: Math.floor(Math.random() * 40) + 60
  }));
}

function getPersonalizedSuggestions(recommendations: any[], userProgress: any[]): any[] {
  return recommendations.slice(0, 6);
} 